import { useState } from 'react'
import type { ReactNode } from 'react'
import { Button, toastManager } from '@systemeio/components'
import { LecturePage as LightLecturePage } from '@systemeio/light-lecture-viewer'
import { ParseKeys } from 'i18next'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { twJoin } from 'tailwind-merge'
import { AccessDenied, getAccessDeniedText } from 'shared/errors/access-denied'
import useUser from 'shared/hooks/use-user'
import { CommentsList } from 'modules/comments/components'
import { CommentsTypeEnum } from 'modules/comments/types'
import { useCompleteCourseLecture } from '../hooks/use-complete-course-lecture'
import { useCourseData } from '../hooks/use-course-data'
import { useCourseMenu } from '../hooks/use-course-menu'
import { useLightViewLecture } from '../hooks/use-light-view-lecture'
import useStepRouter from '../hooks/use-step-router'
import type {
  CourseLectureAccessDeniedDataInterface,
  CourseLectureId,
} from '../types/course-lecture-interface'
import { CourseStepEnum } from '../types/course-step-type'
import { addPublicCustomClassNamePrefix } from '../utils/add-class-prefix'
import { CourseContentSkeleton } from './course-content-skeleton'
import { CourseInfoScreenLayout } from './course-info-screens/course-info-screen-layout'
import { CoursePurchaseModuleScreen } from './course-info-screens/course-purchase-module-screen'
import { CourseStepButton } from './course-state-button'
import CourseTracker from './course-tracker'

interface LightCourseLectureProps {
  lectureId: CourseLectureId
}

export const LightCourseLecture = ({ lectureId }: LightCourseLectureProps): ReactNode => {
  const { t } = useTranslation()
  const router = useRouter()
  const { search, commentId } = router.query

  const [moduleLink, setModuleLink] = useState<string | null>(null)
  const [shouldSendLog, setShouldSendLog] = useState(true)
  const [accessError, setAccessError] = useState<ParseKeys | null>(null)

  const { step, onNextStep } = useStepRouter()
  const { user } = useUser()

  const completeCourseLecture = useCompleteCourseLecture()
  const getCourseDataQuery = useCourseData()
  const getCourseMenuQuery = useCourseMenu({
    courseId: getCourseDataQuery.courseData?.id,
  })
  const getLightLectureQuery = useLightViewLecture({
    lectureId: lectureId.toString(),
    searchQuery: search,
    config: {
      isPaused: () => getCourseDataQuery.courseData?.courseType !== 'light_course',
      onError: error => {
        if (
          error instanceof AccessDenied &&
          typeof error.response?.data === 'object' &&
          error.response?.data !== null &&
          'reason' in (error.response?.data ?? {})
        ) {
          setAccessError(getAccessDeniedText(error.response?.data.reason))
        } else if (error instanceof AccessDenied) {
          const data = error.response?.data as
            | CourseLectureAccessDeniedDataInterface
            | null
            | undefined
          if (data && 'module' in data) {
            // NOTE: sales page for module
            setModuleLink(data.module as string)
          }
        } else {
          toastManager.add({ type: 'error', title: t('global.error') })
        }
        setShouldSendLog(false)
      },
    },
  })

  const onCompleteCourseLecture = async (): Promise<void> => {
    if (step?.id) {
      await completeCourseLecture.completeCourseLecture(step.id)
      await getCourseMenuQuery.mutate(
        data =>
          data?.map(module => ({
            ...module,
            lectures: module.lectures.map(lecture =>
              lecture.id === step?.id ? { ...lecture, completed: true } : lecture,
            ),
          })),
        false,
      )
      getCourseDataQuery.mutate()
      onNextStep()
    }
  }

  const loggingEnabled =
    !!getCourseDataQuery.courseData?.lectureSessionLogging &&
    !!getLightLectureQuery.data &&
    shouldSendLog

  const isAccessLocked =
    !!step &&
    step.type === CourseStepEnum.lecture &&
    !step.available &&
    (step.unavailabilityReason === 'not_included_in_partial_access' ||
      step.unavailabilityReason === 'not_enrolled' ||
      step.unavailabilityReason === 'purchase_required')

  const displayPurchaseScreen =
    !getLightLectureQuery.isValidating && !!step && (isAccessLocked || !!moduleLink)

  if (displayPurchaseScreen) return <CoursePurchaseModuleScreen moduleLink={moduleLink} />

  const isLoading = (getLightLectureQuery.isLoading || !getLightLectureQuery.data) && !accessError

  if (isLoading && !getLightLectureQuery.data) return <CourseContentSkeleton />

  if (accessError)
    return (
      <CourseInfoScreenLayout
        text={<span className="whitespace-pre-line">{t(accessError)}</span>}
      />
    )

  return (
    <div
      className={twJoin(
        'relative w-full',
        addPublicCustomClassNamePrefix('course-lecture__container'),
      )}
    >
      {loggingEnabled && <CourseTracker />}

      <div className="relative w-full overflow-y-auto rounded-md bg-THEME-block-background p-4 [&>*::-webkit-scrollbar]:hidden">
        {getLightLectureQuery.data && (
          <LightLecturePage
            name={getLightLectureQuery.data.name}
            body={getLightLectureQuery.data.body}
            video={getLightLectureQuery.data.video}
            attachments={getLightLectureQuery.data.attachments}
            // This feature needs for preview in Dashboard, not for us
            showPlaceholderComments={false}
            disableDownloading={getCourseDataQuery.courseData?.disableRightClick}
          />
        )}

        {user && getLightLectureQuery.data?.showComments && (
          <div
            className={twJoin(
              `px-3 py-4 lg:px-4 lg:py-5`,
              addPublicCustomClassNamePrefix('course-lecture__comments-container'),
            )}
          >
            <CommentsList
              commentsType={CommentsTypeEnum.LightLecture}
              onError={() =>
                toastManager.add({
                  type: 'error',
                  title: t('comments.error.error-loading-comments'),
                })
              }
              lightLecture={{ lectureId: getLightLectureQuery.data.id }}
              targetCommentId={commentId ? Number(commentId) : undefined}
            />
          </div>
        )}
      </div>

      <div className="mt-5 flex justify-center rounded-md bg-THEME-block-background p-6">
        {step?.completed ? (
          <CourseStepButton />
        ) : (
          <Button
            variant="secondary"
            isLoading={completeCourseLecture.isFetching}
            className={twJoin(
              'border-THEME-primary! bg-THEME-block-background! text-THEME-primary! hover:border-THEME-block-background! hover:bg-THEME-primary! hover:text-THEME-block-background! disabled:border-THEME-disabled! disabled:text-THEME-disabled!',
              'font-bold',
              addPublicCustomClassNamePrefix([
                'course__button',
                'course__button--secondary',
                'course__button--mark-as-completed',
              ]),
            )}
            onClick={onCompleteCourseLecture}
          >
            {t('course.mark_step_as_completed_button_label')}
          </Button>
        )}
      </div>
    </div>
  )
}
